<template>
  <div class="asr-create-or-reform">
    <q-linear-progress v-if="isPrefillLoading" indeterminate class="tw-mb-2" />

    <q-banner
      v-if="contextLine"
      dense
      rounded
      class="tw-mb-3 tw-bg-grey-2 tw-text-grey-9"
    >
      <template #avatar>
        <q-icon name="info" color="primary" />
      </template>
      {{ contextLine }}
    </q-banner>

    <q-banner
      v-if="hasPrevAsrHint"
      dense
      rounded
      class="tw-mb-3 tw-bg-blue-1 tw-text-grey-9"
    >
      <template #avatar>
        <q-icon name="auto_fix_high" color="primary" />
      </template>
      Данные предзаполнены по последней АСР подрядчика в этом ЖК
      <span v-if="prevAsrLevelNum"> ({{ prevAsrLevelNum }})</span>.
    </q-banner>

    <div v-if="showAsrFloorFields" class="form-1c form-1c--floor tw-mb-2">
      <CustomSelect
        label="Этажи"
        :options="sortedFloorOptions"
        optionValueKey="floor"
        optionLabelKey="floor_label"
        placeholder="Выберите один или несколько этажей"
        :multiple="true"
        :fullWidth="true"
        :value="asrSelectedFloors"
        @change="onAsrFloorsChange"
      />
    </div>

    <q-form class="form-1c" @submit.prevent="onSubmit">
      <div class="form-1c__row">
        <label class="form-1c__label">
          Адрес объекта <span class="required">*</span>
        </label>
        <div class="form-1c__field">
          <CustomInput
            type="textarea"
            :rows="3"
            :required="true"
            :value="resident_address"
            placeholder="Адрес объекта"
            @change_input="(v) => (resident_address = v)"
          />
        </div>
      </div>

      <div class="form-1c__row">
        <label class="form-1c__label">
          Номер документа <span class="required">*</span>
        </label>
        <div class="form-1c__field">
          <CustomInput
            type="text"
            :required="true"
            :value="document_num"
            :placeholder="
              documentNumPlaceholder
                ? `Предыдущий номер: ${documentNumPlaceholder}`
                : 'Введите номер документа'
            "
            @change_input="(v) => (document_num = v)"
          />
        </div>
      </div>

      <div class="form-1c__row">
        <label class="form-1c__label">
          Дата документа <span class="required">*</span>
        </label>
        <div class="form-1c__field">
          <CustomDate
            :required="true"
            :full-width="true"
            placeholder="ДД.ММ.ГГГГ"
            :value="document_date"
            :max-date="documentMaxDate"
            :default-value="documentMaxDate"
            @change_date="(v) => (document_date = v)"
          />
        </div>
      </div>

      <div class="form-1c__row">
        <label class="form-1c__label">
          Идентификационные параметры эскиза
          <span class="required">*</span>
        </label>
        <div class="form-1c__field">
          <CustomInput
            :required="true"
            :value="work_comment"
            placeholder="429-4-18-КЖ Лист 25-35"
            @change_input="(v) => (work_comment = v)"
          />
        </div>
      </div>

      <div class="form-1c__row">
        <label class="form-1c__label">
          К освидетельствованию предъявлены работы
          <span class="required">*</span>
        </label>
        <div class="form-1c__field">
          <CustomInput
            type="textarea"
            :rows="3"
            :required="true"
            :value="work_sets"
            placeholder="Наименование скрытых работ"
            @change_input="(v) => (work_sets = v)"
          />
        </div>
      </div>

      <div class="form-1c__row">
        <label class="form-1c__label">
          При выполнении работ применены <span class="required">*</span>
        </label>
        <div class="form-1c__field">
          <CustomInput
            type="textarea"
            :rows="3"
            :required="true"
            :value="materials"
            placeholder="Наименование материалов, конструкций, изделий и т.д."
            @change_input="(v) => (materials = v)"
          />
        </div>
      </div>

      <div class="form-1c__row">
        <label class="form-1c__label">
          Разрешается производство последующих работ
          <span class="required">*</span>
        </label>
        <div class="form-1c__field">
          <CustomInput
            type="textarea"
            :rows="3"
            :required="true"
            :value="work_asr_name"
            placeholder="Наименование последующих работ и конструкций"
            @change_input="(v) => (work_asr_name = v)"
          />
        </div>
      </div>

      <div class="form-1c__row">
        <label class="form-1c__label">
          Дата начала работ <span class="required">*</span>
        </label>
        <div class="form-1c__field">
          <CustomDate
            :required="true"
            :full-width="true"
            placeholder="ДД.ММ.ГГГГ"
            :value="work_date_begin"
            @change_date="(v) => (work_date_begin = v)"
          />
        </div>
      </div>

      <div class="form-1c__row">
        <label class="form-1c__label">
          Дата окончания работ <span class="required">*</span>
        </label>
        <div class="form-1c__field">
          <CustomDate
            :required="true"
            :full-width="true"
            placeholder="ДД.ММ.ГГГГ"
            :value="work_date_end"
            @change_date="(v) => (work_date_end = v)"
          />
        </div>
      </div>

      <CustomModalFooter
        :submit-btn-text="submitBtnText"
        :btn-loading="isSubmitting"
      />
    </q-form>
  </div>
</template>

<script>
import CustomInput from "@/components/form/CustomInput";
import CustomDate from "@/components/form/CustomDate";
import CustomSelect from "@/components/form/CustomSelect";
import CustomModalFooter from "@/components/modal/modals/modal-footer";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { getLocaleDate } from "@/services";
import {
  createAsrManual,
  reformAsrManual,
  getLastAsrForContractor,
  getAsrDocInfo,
} from "@/views/ResidentialSettings/services";

export default {
  name: "AsrCreateOrReform",
  components: {
    CustomInput,
    CustomDate,
    CustomSelect,
    CustomModalFooter,
  },
  props: {
    data: { type: Object, required: true },
    closeModal: { type: Function, required: true },
  },
  data() {
    return {
      resident_address: "",
      document_num: "",
      documentNumPlaceholder: "",
      document_date: getLocaleDate(new Date()),
      work_date_begin: "",
      work_date_end: "",
      work_comment: "",
      work_sets: "",
      materials: "",
      work_asr_name: "",
      isSubmitting: false,
      isPrefillLoading: false,
      prevAsrLevelNum: null,
      prefilledFromPrev: false,
      asrSelectedFloors: [],
    };
  },
  setup() {
    const { showErrorSnackbar, showSucceedSnackbar } = useCustomSnackbar();
    return { showErrorSnackbar, showSucceedSnackbar };
  },
  computed: {
    isReform() {
      return this.data?.mode === "reform";
    },
    submitBtnText() {
      return this.isReform ? "Пересобрать" : "Создать";
    },
    documentMaxDate() {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d;
    },
    contextLine() {
      const parts = [];
      if (this.data?.residentName) parts.push(this.data.residentName);
      if (this.data?.contractorName) {
        parts.push(`Подрядчик: ${this.data.contractorName}`);
      }
      const loc = [];
      if (this.showAsrFloorFields) {
        const sfx = this.asrFloorContextSuffix;
        if (sfx) loc.push(sfx);
      } else {
        if (this.data?.blockName) loc.push(`Блок ${this.data.blockName}`);
        if (this.data?.levelNum != null) loc.push(`Этаж ${this.data.levelNum}`);
      }
      if (loc.length) parts.push(loc.join(", "));
      return parts.join(" · ");
    },
    asrFloorContextSuffix() {
      if (!this.showAsrFloorFields) return "";
      const floors = (this.asrSelectedFloors || [])
        .map((f) => Number(f))
        .filter((n) => !Number.isNaN(n))
        .sort((a, b) => a - b);
      if (!floors.length) return "";
      const labels = floors.map((f) => this.asrFloorLabelByValue(f));
      if (labels.length === 1) {
        return labels[0];
      }
      return `Этажи: ${labels.join(", ")}`;
    },
    hasPrevAsrHint() {
      return this.prefilledFromPrev;
    },
    sortedFloorOptions() {
      const raw = this.data?.floorOptions;
      if (!Array.isArray(raw)) return [];
      return [...raw].sort(
        (a, b) => Number(a?.floor) - Number(b?.floor)
      );
    },
    showAsrFloorFields() {
      return (
        !this.isReform &&
        this.data?.enableAsrFloorRange === true &&
        this.sortedFloorOptions.length > 0
      );
    },
  },
  async mounted() {
    let ids = null;
    if (this.isReform) {
      ids = await this.loadCurrentDoc();
    }
    if (this.isReform && !!ids) {
      if (!String(this.resident_address || "").trim()) {
        await this.applyPrevAsrPrefill({ addressOnly: true });
      }
      return;
    }
    await this.applyPrevAsrPrefill();
  },
  methods: {
    async loadCurrentDoc() {
      const docId = this.data?.reformFloorMapDocumentId;
      if (!docId) return null;
      this.isPrefillLoading = true;
      const row = await getAsrDocInfo(docId);
      this.isPrefillLoading = false;
      if (!row) return null;
      this.applyResidentAddress(row.resident_address);
      this.document_num = row.document_num || "";
      this.document_date = row.document_date || this.document_date;
      this.work_date_begin = row.work_date_begin || "";
      this.work_date_end = row.work_date_end || "";
      this.work_comment = row.work_comment || "";
      this.work_sets = row.work_sets || "";
      this.materials = row.materials || "";
      this.work_asr_name = row.work_asr_name || "";
      return true
    },
    async applyPrevAsrPrefill({ addressOnly = false } = {}) {
      this.isPrefillLoading = true;
      const res = await getLastAsrForContractor({
        resident_id: this.data?.residentId,
        contractor_id: this.data?.contractorId,
      });
      this.isPrefillLoading = false;
      if (!res) return;
      this.applyResidentAddress(res.resident_address);
      if (addressOnly || !res.floor_map_document_asr_id) return;
      this.documentNumPlaceholder = res.document_num || "";
      if (!this.materials) this.materials = res.materials || "";
      if (!this.work_asr_name) this.work_asr_name = res.work_asr_name || "";
      if (!this.work_date_begin)
        this.work_date_begin = res.work_date_begin || "";
      if (!this.work_date_end) this.work_date_end = res.work_date_end || "";
      if (!this.work_comment) this.work_comment = res.work_comment || "";
      if (!this.work_sets) this.work_sets = res.work_sets || "";
      this.prevAsrLevelNum =
        `${res?.block_name} блок, ${res.level_num} этаж` || null;
      this.prefilledFromPrev = true;
    },
    applyResidentAddress(value) {
      if (String(this.resident_address || "").trim()) return;
      this.resident_address = value || "";
    },
    asrFloorLabelByValue(floorVal) {
      const row = this.floorOptionByValue(floorVal);
      if (!row) return `Этаж ${floorVal}`;
      if (row.floor_name) return row.floor_name;
      if (row.floor_label) return row.floor_label;
      return `Этаж ${floorVal}`;
    },
    floorOptionByValue(floorVal) {
      return this.sortedFloorOptions.find(
        (o) => String(o?.floor) === String(floorVal)
      );
    },
    floorValueHasLinkedAsr(floorVal) {
      const o = this.floorOptionByValue(floorVal);
      if (!o) return false;
      if (o.has_linked_asr === true) return true;
      const id = o.linked_asr_floor_map_document_id;
      return id != null && id !== "" && !Number.isNaN(Number(id));
    },
    normalizeAsrFloorSelection(value) {
      const arr = Array.isArray(value)
        ? value
        : value != null && value !== ""
          ? [value]
          : [];
      return arr
        .map((x) => Number(x))
        .filter((n) => !Number.isNaN(n));
    },
    onAsrFloorsChange(value) {
      this.asrSelectedFloors = this.normalizeAsrFloorSelection(value);
    },
    resolveHelpCallIdsForCreate() {
      const fromArray = Array.isArray(this.data?.helpCallIds)
        ? this.data.helpCallIds.filter(
            (id) => id != null && String(id).trim() !== ""
          )
        : [];
      let ids =
        fromArray.length > 0
          ? [...new Set(fromArray.map((id) => Number(id)))]
          : this.data?.helpCallId != null && this.data?.helpCallId !== ""
            ? [Number(this.data.helpCallId)]
            : [];
      if (
        !this.showAsrFloorFields ||
        !Array.isArray(this.data?.helpCallsWithFloors) ||
        !this.data.helpCallsWithFloors.length
      ) {
        return ids;
      }
      const floorSet = new Set(
        (this.asrSelectedFloors || [])
          .map((f) => Number(f))
          .filter((n) => !Number.isNaN(n))
      );
      if (!floorSet.size) return ids;
      const inRange = new Set(
        this.data.helpCallsWithFloors
          .filter((row) => {
            const ln = Number(row.level_num);
            if (Number.isNaN(ln)) return false;
            return floorSet.has(ln);
          })
          .map((row) => Number(row.help_call_id))
      );
      return ids.filter((id) => inRange.has(id));
    },
    validateForm() {
      if (this.showAsrFloorFields) {
        if (!this.asrSelectedFloors?.length) {
          this.showErrorSnackbar("Выберите этаж или несколько этажей");
          return false;
        }
      }
      if (!String(this.resident_address || "").trim()) {
        this.showErrorSnackbar("Укажите адрес объекта");
        return false;
      }
      if (!String(this.document_num || "").trim()) {
        this.showErrorSnackbar("Укажите номер документа");
        return false;
      }
      if (!this.document_date) {
        this.showErrorSnackbar("Укажите дату документа");
        return false;
      }
      if (!String(this.work_comment || "").trim()) {
        this.showErrorSnackbar("Укажите идентификационные данные эскиза");
        return false;
      }
      if (!String(this.work_sets || "").trim()) {
        this.showErrorSnackbar("Укажите перечень предъявленных работ");
        return false;
      }
      if (!String(this.materials || "").trim()) {
        this.showErrorSnackbar("Укажите применённые материалы");
        return false;
      }
      if (!String(this.work_asr_name || "").trim()) {
        this.showErrorSnackbar("Укажите перечень последующих работ");
        return false;
      }
      if (!this.work_date_begin || !this.work_date_end) {
        this.showErrorSnackbar("Укажите даты начала и окончания работ");
        return false;
      }
      return true;
    },
    buildBody(extra = {}) {
      return {
        resident_address: String(this.resident_address).trim(),
        document_num: String(this.document_num).trim(),
        document_date: this.document_date,
        work_date_begin: this.work_date_begin,
        work_date_end: this.work_date_end,
        work_comment: String(this.work_comment).trim(),
        work_sets: String(this.work_sets).trim(),
        materials: String(this.materials).trim(),
        work_asr_name: String(this.work_asr_name).trim(),
        ...extra,
      };
    },
    async onSubmit() {
      if (!this.validateForm()) return;

      this.isSubmitting = true;
      let res;
      if (this.isReform) {
        const docId = this.data?.reformFloorMapDocumentId;
        if (!docId) {
          this.isSubmitting = false;
          this.showErrorSnackbar("Не указан документ для пересборки");
          return;
        }
        res = await reformAsrManual(
          this.buildBody({ floor_map_document_id: docId }),
          this.data?.documentParams || {}
        );
      } else {
        const helpCallIds = this.resolveHelpCallIdsForCreate();
        if (!helpCallIds.length) {
          this.isSubmitting = false;
          this.showErrorSnackbar(
            this.showAsrFloorFields &&
              Array.isArray(this.data?.helpCallsWithFloors) &&
              this.data.helpCallsWithFloors.length
              ? "В выбранном диапазоне этажей нет вызовов из группы"
              : "Не указаны вызовы для создания АСР"
          );
          return;
        }
        res = await createAsrManual(
          this.buildBody({ help_call_ids: helpCallIds }),
          this.data?.documentParams || {}
        );
      }
      this.isSubmitting = false;
      if (!res) return;

      this.showSucceedSnackbar(
        this.isReform ? "Документ пересобран" : "Документ создан"
      );
      if (typeof this.data?.onSuccess === "function") {
        this.data.onSuccess(res);
      }
      this.closeModal();
    },
  },
};
</script>

<style scoped lang="scss">
.asr-create-or-reform {
  min-width: 750px;
  max-width: 100%;

  @media (max-width: 768px) {
    min-width: 100%;
  }

  .form-1c {
    margin-top: 8px;

    &__row {
      margin-bottom: 12px;
    }

    &__label {
      margin-bottom: 6px;
      display: block;
      font-size: 14px;

      .required {
        color: red;
      }
    }

    &__field {
      min-width: 0;
    }
  }
}
</style>
